import { FormEvent, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Confetti from 'react-confetti';
import {
  ArrowLeft,
  Calendar,
  Euro,
  FileText,
  PlusCircle,
  Search,
  Sparkles,
  Trophy,
  UserPlus,
  UserRoundSearch,
} from 'lucide-react';
import { clientsApi, salesApi } from '@/lib/api';
import { formatCurrency, formatLocalDateInput } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const saleSchema = z.object({
  clientId: z.string().min(1, 'Selecciona o crea un cliente antes de registrar la venta'),
  amount: z.number().min(0.01, 'El monto debe ser mayor a 0'),
  saleDate: z.string().min(1, 'La fecha es requerida'),
  notes: z.string().optional(),
});

type SaleFormData = z.infer<typeof saleSchema>;

type ClientFormData = {
  name: string;
  cif: string;
  phone: string;
  email: string;
  notes: string;
};

type Client = {
  id: string;
  name: string;
  cif: string;
  phone?: string;
  email?: string;
  notes?: string;
};

type SaleItem = {
  id: string;
  amount: number;
  saleDate: string;
  notes?: string;
  client?: Client;
};

const EMPTY_CLIENT_FORM: ClientFormData = {
  name: '',
  cif: '',
  phone: '',
  email: '',
  notes: '',
};

const MOTIVATION_QUOTES = [
  'Cada llamada es una oportunidad. La siguiente puede ser la grande.',
  'Las ventas consistentes ganan al sprint puntual.',
  'Hoy no persigues suerte: ejecutas proceso y cierras.',
  'Una venta más te acerca a tu mejor mes.',
  'Disciplina diaria, resultados mensuales.',
];

function getMonthDateRange(date: Date) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    startDate: formatLocalDateInput(start),
    endDate: formatLocalDateInput(end),
  };
}

function toClientPayload(form: ClientFormData) {
  const phone = form.phone.trim();
  const email = form.email.trim();
  const notes = form.notes.trim();

  return {
    name: form.name.trim(),
    cif: form.cif.trim().toUpperCase(),
    phone: phone || undefined,
    email: email || undefined,
    notes: notes || undefined,
  };
}

export function NewSalePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'client' | 'sale'>('client');
  const [showConfetti, setShowConfetti] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showExistingClients, setShowExistingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientForm, setClientForm] = useState<ClientFormData>(EMPTY_CLIENT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [motivation, setMotivation] = useState(MOTIVATION_QUOTES[0]);
  const [viewport, setViewport] = useState({ width: 1280, height: 720 });

  const { startDate, endDate } = getMonthDateRange(new Date());

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SaleFormData>({
    resolver: zodResolver(saleSchema),
    defaultValues: {
      clientId: '',
      amount: 0,
      saleDate: formatLocalDateInput(new Date()),
      notes: '',
    },
  });

  const amount = watch('amount');
  const safeAmount = Number.isFinite(amount) ? amount : 0;

  const { data: clientsData } = useQuery({
    queryKey: ['clients-search', clientSearch],
    queryFn: () => clientsApi.search(clientSearch),
    enabled: showExistingClients && clientSearch.trim().length >= 2,
  });

  const { data: recentSalesData } = useQuery({
    queryKey: ['sales-recent-new-sale'],
    queryFn: () => salesApi.getRecent(4),
  });

  const { data: todayStatsData } = useQuery({
    queryKey: ['sales-today-new-sale'],
    queryFn: () => salesApi.getToday(),
  });

  const { data: monthStatsData } = useQuery({
    queryKey: ['sales-month-stats-new-sale'],
    queryFn: () => salesApi.getStats('month'),
  });

  const { data: monthSalesData } = useQuery({
    queryKey: ['sales-month-list-new-sale', startDate, endDate],
    queryFn: () =>
      salesApi.getAll({
        page: 1,
        limit: 100,
        startDate,
        endDate,
        sortBy: 'saleDate',
        sortOrder: 'DESC',
      }),
  });

  const createClientMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof toClientPayload>) => clientsApi.create(payload),
    onSuccess: (response) => {
      const createdClient = response.data as Client;
      setSelectedClient(createdClient);
      setValue('clientId', createdClient.id, { shouldValidate: true });
      setClientForm(EMPTY_CLIENT_FORM);
      setShowExistingClients(false);
      setClientSearch('');
      setActiveTab('sale');

      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients-search'] });

    },
  });

  const clients: Client[] = clientsData?.data || [];
  const recentSales: SaleItem[] = recentSalesData?.data || [];
  const todayStats = todayStatsData?.data || { total: 0, count: 0, average: 0 };
  const monthlyStats = monthStatsData?.data || { total: 0, count: 0, average: 0 };

  const topClientMonth = useMemo(() => {
    const monthSales: SaleItem[] = monthSalesData?.data?.data ?? [];
    const totals = new Map<string, { name: string; total: number }>();

    for (const sale of monthSales) {
      const clientName = sale.client?.name || 'Cliente sin nombre';
      const current = totals.get(clientName) || { name: clientName, total: 0 };
      current.total += Number(sale.amount || 0);
      totals.set(clientName, current);
    }

    const sorted = Array.from(totals.values()).sort((a, b) => b.total - a.total);
    return sorted[0] || null;
  }, [monthSalesData?.data?.data]);

  useEffect(() => {
    const pickRandomMessage = () => {
      const random = MOTIVATION_QUOTES[Math.floor(Math.random() * MOTIVATION_QUOTES.length)];
      setMotivation(random);
    };

    pickRandomMessage();
    const timer = setInterval(pickRandomMessage, 12000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const updateViewport = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setValue('clientId', client.id, { shouldValidate: true });
    setClientSearch('');
    setShowExistingClients(false);
    setActiveTab('sale');
  };

  const handleClientSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const payload = toClientPayload(clientForm);

    if (!payload.name || !payload.cif) {
      return;
    }

    try {
      await createClientMutation.mutateAsync(payload);
    } catch (error) {
      console.error('Failed to create client', error);
    }
  };

  const onSubmit = async (data: SaleFormData) => {
    setIsSubmitting(true);
    try {
      await salesApi.create(data);
      setShowConfetti(true);
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['sales-stats'] });
      queryClient.invalidateQueries({ queryKey: ['sales-current-month-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['sales-target-month-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['sales-recent-new-sale'] });
      queryClient.invalidateQueries({ queryKey: ['sales-today-new-sale'] });
      queryClient.invalidateQueries({ queryKey: ['sales-month-stats-new-sale'] });
      queryClient.invalidateQueries({ queryKey: ['sales-month-list-new-sale'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['user-target-current'] });

      window.setTimeout(() => {
        setShowConfetti(false);
        navigate('/dashboard');
      }, 1200);
    } catch (error) {
      console.error('Failed to create sale', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickAmount = (multiplier: number) => {
    const baseAmount = 100;
    setValue('amount', baseAmount * multiplier);
  };

  return (
    <div className="max-w-5xl mx-auto">
      <AnimatePresence>
        {showConfetti && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 pointer-events-none"
          >
            <Confetti
              width={viewport.width}
              height={viewport.height}
              recycle={false}
              numberOfPieces={700}
              gravity={0.08}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <Button variant="ghost" onClick={() => navigate('/sales')} className="mb-2">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver a Ventas
          </Button>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Nueva Venta
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Primero fija el cliente y luego registra la venta sin salir de esta pantalla.
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setActiveTab('client')}
                className={`rounded-xl px-4 py-3 text-left transition-all ${
                  activeTab === 'client'
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <p className="text-sm font-medium opacity-80">Paso 1</p>
                <p className="text-base font-semibold">Cliente</p>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!selectedClient) return;
                  setActiveTab('sale');
                }}
                className={`rounded-xl px-4 py-3 text-left transition-all ${
                  activeTab === 'sale'
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                    : selectedClient
                      ? 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                      : 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                }`}
                disabled={!selectedClient}
              >
                <p className="text-sm font-medium opacity-80">Paso 2</p>
                <p className="text-base font-semibold">Venta</p>
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'client' ? (
              <motion.section
                key="client-tab"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                className="space-y-6"
              >
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-6">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                        <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Cliente para esta venta
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Crea uno nuevo o recupera uno ya existente.
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant={showExistingClients ? 'outline' : 'default'}
                      onClick={() => setShowExistingClients((prev) => !prev)}
                    >
                      <UserRoundSearch className="w-4 h-4 mr-2" />
                      {showExistingClients ? 'Ocultar búsqueda' : 'Seleccionar existente'}
                    </Button>
                  </div>

                  {selectedClient ? (
                    <div className="mb-6 flex flex-col gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                          Cliente actual
                        </p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {selectedClient.name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          CIF: {selectedClient.cif}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setSelectedClient(null);
                          setValue('clientId', '', { shouldValidate: true });
                        }}
                      >
                        Cambiar cliente
                      </Button>
                    </div>
                  ) : null}

                  <AnimatePresence>
                    {showExistingClients && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-800/60"
                      >
                        <div className="relative mb-3">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <Input
                            type="text"
                            placeholder="Buscar cliente por nombre o CIF..."
                            value={clientSearch}
                            onChange={(event) => setClientSearch(event.target.value)}
                            className="pl-10"
                          />
                        </div>

                        {clientSearch.trim().length < 2 ? (
                          <p className="text-sm text-gray-500">
                            Escribe al menos 2 caracteres para buscar clientes existentes.
                          </p>
                        ) : clients.length === 0 ? (
                          <p className="text-sm text-gray-500">No se encontraron clientes.</p>
                        ) : (
                          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            {clients.map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => handleClientSelect(client)}
                                className="w-full p-3 text-left hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-between"
                              >
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {client.name}
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    {client.cif}
                                  </p>
                                </div>
                                <PlusCircle className="w-4 h-4 text-gray-400" />
                              </button>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <form onSubmit={handleClientSubmit} className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Nombre *</label>
                      <Input
                        value={clientForm.name}
                        onChange={(event) =>
                          setClientForm((prev) => ({ ...prev, name: event.target.value }))
                        }
                        placeholder="Empresa SL"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">CIF *</label>
                      <Input
                        value={clientForm.cif}
                        onChange={(event) =>
                          setClientForm((prev) => ({ ...prev, cif: event.target.value }))
                        }
                        placeholder="B12345678"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Teléfono</label>
                        <Input
                          value={clientForm.phone}
                          onChange={(event) =>
                            setClientForm((prev) => ({ ...prev, phone: event.target.value }))
                          }
                          placeholder="+34..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Email</label>
                        <Input
                          type="email"
                          value={clientForm.email}
                          onChange={(event) =>
                            setClientForm((prev) => ({ ...prev, email: event.target.value }))
                          }
                          placeholder="cliente@empresa.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Notas</label>
                      <textarea
                        value={clientForm.notes}
                        onChange={(event) =>
                          setClientForm((prev) => ({ ...prev, notes: event.target.value }))
                        }
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        rows={4}
                        placeholder="Información adicional"
                      />
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <Button type="submit" loading={createClientMutation.isPending}>
                        Crear cliente y continuar
                      </Button>
                    </div>
                  </form>
                </div>
              </motion.section>
            ) : (
              <motion.form
                key="sale-tab"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ delay: 0.05 }}
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Cliente asignado
                      </h3>
                      {selectedClient ? (
                        <div className="mt-2">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {selectedClient.name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            CIF: {selectedClient.cif}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                          Todavía no has elegido cliente. Vuelve al paso 1.
                        </p>
                      )}
                    </div>
                    <Button type="button" variant="outline" onClick={() => setActiveTab('client')}>
                      Cambiar cliente
                    </Button>
                  </div>

                  {errors.clientId && (
                    <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                      {errors.clientId.message}
                    </p>
                  )}
                  <input type="hidden" {...register('clientId')} />
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mr-3">
                      <Euro className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Monto
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Importe de la venta
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        {...register('amount', { valueAsNumber: true })}
                        className="pl-10 text-2xl font-bold"
                        placeholder="0.00"
                      />
                    </div>
                    {errors.amount && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {errors.amount.message}
                      </p>
                    )}

                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Cantidades rápidas:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 5, 10, 20, 50].map((multiplier) => (
                          <button
                            key={multiplier}
                            type="button"
                            onClick={() => handleQuickAmount(multiplier)}
                            className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors"
                          >
                            {formatCurrency(100 * multiplier)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {safeAmount > 0 && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Total:</span>
                          <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(safeAmount)}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          IVA incluido (21%): {formatCurrency(safeAmount * 0.21)}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mr-3">
                        <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Fecha
                      </h3>
                    </div>
                    <Input type="date" {...register('saleDate')} className="w-full" />
                    {errors.saleDate && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {errors.saleDate.message}
                      </p>
                    )}
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-3">
                        <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Notas
                      </h3>
                    </div>
                    <textarea
                      {...register('notes')}
                      className="w-full h-32 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Detalle de la venta, contexto y próximos pasos..."
                    />
                  </div>
                </div>

                <div className="sticky bottom-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-xl">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Total a registrar:</p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(safeAmount)}
                      </p>
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-emerald-500/25"
                      loading={isSubmitting}
                      disabled={!selectedClient || safeAmount <= 0}
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Registrar Venta
                    </Button>
                  </div>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
            <h3 className="text-lg font-semibold mb-2 flex items-center">
              <Sparkles className="w-5 h-5 mr-2" />
              Mensaje del momento
            </h3>
            <p className="text-sm leading-relaxed opacity-95">{motivation}</p>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Resumen real
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Ventas hoy</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {todayStats.count}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total hoy</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(todayStats.total)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Promedio hoy</span>
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  {formatCurrency(todayStats.average)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Ventas del mes</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {monthlyStats.count}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Trophy className="w-5 h-5 mr-2 text-amber-500" />
              Mejores ventas del mes
            </h3>

            {topClientMonth ? (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-700 dark:text-amber-300">Cliente líder</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {topClientMonth.name}
                </p>
                <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                  {formatCurrency(topClientMonth.total)}
                </p>
              </div>
            ) : null}

            <div className="space-y-3">
              {recentSales.length === 0 ? (
                <p className="text-sm text-gray-500">No hay ventas recientes.</p>
              ) : (
                recentSales.map((sale) => (
                  <div
                    key={sale.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {sale.client?.name || 'Cliente'}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {new Date(sale.saleDate).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <p className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(sale.amount)}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
